import { useEffect, useRef, type CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import { outlineLabel, type GuideSection, type OutlineEntry } from '../lib/helpSearch';

interface GuideOutlineProps {
  outline: OutlineEntry[];
  /** The section under the reading line, however deep. */
  activeIndex: number | null;
  /** The `##` section that contains it (or is it). */
  activeParentIndex: number | null;
  /** Which `##` entries show their parts. */
  expanded: Set<number>;
  onToggle: (index: number) => void;
  onSelect: (section: GuideSection) => void;
  className?: string;
  style?: CSSProperties;
}

/**
 * The guide's table of contents: every `##` section, opening to its `###`
 * parts. HelpTab owns the state; this only draws it and reports clicks.
 */
export function GuideOutline({
  outline,
  activeIndex,
  activeParentIndex,
  expanded,
  onToggle,
  onSelect,
  className = '',
  style,
}: GuideOutlineProps) {
  const navRef = useRef<HTMLElement>(null);

  // When the list itself scrolls, keep the entry being read in view. Only the
  // list moves — never the page, which is mid-scroll when this fires.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || activeIndex === null) return;
    const el = nav.querySelector<HTMLElement>(`[data-outline-index="${activeIndex}"]`);
    if (!el) return;
    const navRect = nav.getBoundingClientRect();
    const rect = el.getBoundingClientRect();
    if (rect.top < navRect.top) nav.scrollTop -= navRect.top - rect.top;
    else if (rect.bottom > navRect.bottom) nav.scrollTop += rect.bottom - navRect.bottom;
  }, [activeIndex, expanded]);

  return (
    <nav
      ref={navRef}
      aria-label="Guide contents"
      data-testid="help-guide-outline"
      className={`overflow-y-auto ${className}`}
      style={style}
    >
      <ul className="space-y-0.5">
        {outline.map(({ section, children }) => {
          const { number, title } = outlineLabel(section.heading);
          const isOpen = expanded.has(section.index);
          const isActive = section.index === activeParentIndex;
          return (
            <li key={section.index}>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-outline-index={section.index}
                  onClick={() => onSelect(section)}
                  className={`flex-1 flex items-baseline gap-2 text-left rounded px-2 py-1.5 text-sm leading-snug hover:bg-gray-100 ${
                    isActive ? 'font-semibold text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span className="w-4 flex-shrink-0 text-xs tabular-nums text-gray-400">{number}</span>
                  <span>{title}</span>
                </button>
                {children.length > 0 && (
                  <button
                    type="button"
                    onClick={() => onToggle(section.index)}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? 'Hide' : 'Show'} the parts of ${title}`}
                    className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  >
                    <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              {isOpen && children.length > 0 && (
                <ul className="ml-4 mb-1 border-l border-gray-200 pl-2 space-y-0.5">
                  {children.map((child) => (
                    <li key={child.index}>
                      <button
                        type="button"
                        data-outline-index={child.index}
                        onClick={() => onSelect(child)}
                        className={`block w-full text-left rounded px-2 py-1 text-[13px] leading-snug hover:bg-gray-100 ${
                          child.index === activeIndex ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-600'
                        }`}
                      >
                        {child.heading}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
